import getProgressBarColor from "../../helpers/getProgressBarColor";
import Icon from "../../../../../../Elements/Icon/Icon";
import Progressbar from "../../../../../../Reusable/Progressbar/Progressbar";
import { useContent } from "../../../../../../../contexts/contentContext/contentContext";
import { useUser } from "../../../../../../../contexts/userContext/userContext";
import { LinkToNewTab } from "../../../../../../Elements/Link/Link";
import { useEffect, useState } from "react";
import useInbox from "hooks/useInbox";

import styles from "./styles.module.scss";

const ICON_LIFETIME_MS = 3000;

interface StorageSpaceDataI {
  sizeInUse: {
    size: number;
    type: string | null;
  };
  sizeMax: {
    size: number;
    type: string | null;
  };
  percentageOfSpace: number;
  sizeInUseInKb: number;
}

/**
 * Renders the storage space data component.
 * This component displays the user's storage space usage
 * and provides a link to manage the storage.
 */
const StorageSpaceData = (props: StorageSpaceDataI) => {
  const { sizeInUse, sizeMax, percentageOfSpace, sizeInUseInKb } = props;
  const { getTextByKey } = useContent();
  const { isSubscribed, getUserData } = useUser();
  const [prevSize, setPrevSize] = useState<number>(sizeInUseInKb);
  const [storageAction, setStorageAction] = useState<
    "increased" | "decrease" | null
  >(null);
  const { setPageCategory, setSearchParams } = useInbox();

  /**
   * Subscribe for used storage size changes and show an icon in front of the progress bar
   * to indicate if the storage size has increased or decreased.
   */
  useEffect(() => {
    if (sizeInUseInKb !== prevSize) {
      // Logic to handle the difference between current and previous size
      const diff = prevSize !== null ? sizeInUseInKb - prevSize : 0;

      if (diff > 0) {
        setStorageAction("increased");
      } else if (diff < 0) {
        setStorageAction("decrease");
      }

      // Hide the icon after a certain time
      setTimeout(() => {
        setStorageAction(null);
      }, ICON_LIFETIME_MS);

      setPrevSize(sizeInUseInKb);
    }
  }, [sizeInUseInKb, prevSize]);

  // Don't render the component if the user has no storage space (JM Free)
  if (sizeMax.size === 0) {
    return null;
  }

  return isSubscribed() ? (
    <div className={styles.storageSpaceDataWrapper}>
      <span className={styles.title}>{getTextByKey("storage-title")}</span>
      <div className={styles.storageSpaceInfoWrapper}>
        <div className={styles.userAccountInfo}>
          <div className={styles.userFreeSpace}>
            {sizeInUse.size}
            {sizeInUse.type}/{sizeMax.size}
            {sizeMax.type}{" "}
          </div>
          <span>{getTextByKey("storage-used-text")}</span>
        </div>
        <div className={styles.userProgressbarPercent}>
          {percentageOfSpace}%
        </div>
      </div>
      <div className={styles.userProgressbar}>
        {storageAction ? (
          <Icon
            name={`storage-${storageAction}`}
            showTitle
            iconClass={[styles.storageActionIcon, styles[storageAction]].join(
              " "
            )}
            style={{
              animationDuration: `${ICON_LIFETIME_MS}ms`,
              insetInlineStart: `calc(${percentageOfSpace}% - 12px)`,
            }}
          />
        ) : null}
        <Progressbar
          completed={percentageOfSpace}
          color={getProgressBarColor(percentageOfSpace)}
          height="8px"
          animation={100}
        />
      </div>
      <div
        className={styles.manageStorageLinkWrapper}
        onClick={() => setPageCategory("")}
      >
        {getUserData().subUserRole &&
        getUserData().subUserRole !== "Admin" ? null : (
          /*@ts-ignore*/
          <LinkToNewTab
            page="inbox/manageStorage?t=all&sm=saved"
            onClick={() => {
              setSearchParams({
                t: "all",
                sm: ["saved"],
              });
            }}
          >
            {getTextByKey("manage-storage")}
          </LinkToNewTab>
        )}
      </div>
    </div>
  ) : null;
};

export default StorageSpaceData;
